import { DropdownMenuItem } from "@vg-constellation/angular-17/dropdown";
import { Person } from "@models/estateProfile.model";

export type TrustTypeId = "REVOCABLE" | "IRREVOCABLE" | "";

/** Normalize any trust-type string to our canonical uppercase form. */
function normalizeTrustType(trustType: string): TrustTypeId {
  const upper = trustType?.toUpperCase() || "";
  if (upper === "REVOCABLE" || upper === "IRREVOCABLE") return upper;
  return "";
}

/**
 * Build settlor dropdown items from estate profile people.
 * Accepts raw string trustType (handles ROB's title-case "Revocable"/"Irrevocable").
 * Joint included only when primary + spouse exist AND trust is not irrevocable.
 */
export function buildSettlorItems(
  primaryClient: Person | null | undefined,
  spouses: Person[],
  trustType: string,
): DropdownMenuItem[] {
  if (!primaryClient) return [];
  const items: DropdownMenuItem[] = [];

  items.push({
    labelText: `${primaryClient.firstName} ${primaryClient.lastName}`,
    id: "PRIMARY",
  });

  for (const spouse of spouses) {
    items.push({
      labelText: `${spouse.firstName} ${spouse.lastName}`,
      id: spouse.uuid || "SECONDARY",
    });
  }

  const normalized = normalizeTrustType(trustType);
  if (spouses.length > 0 && normalized !== "IRREVOCABLE") {
    items.push({ labelText: "Joint", id: "JOINT" });
  }

  return items;
}

/**
 * Filter a pre-built settlor list for trust type constraints.
 * Use when caller receives settlors via @Input() without Person data.
 * Accepts raw string trustType (handles title-case from ROB).
 */
export function filterSettlorsForTrustType(
  settlors: DropdownMenuItem[],
  trustType: string,
): DropdownMenuItem[] {
  const normalized = normalizeTrustType(trustType);
  if (normalized === "IRREVOCABLE") {
    return settlors.filter((s) => s.id !== "JOINT");
  }
  return settlors;
}

/**
 * Clear settlor if it's invalid for the given trust type.
 * Returns "" if JOINT + IRREVOCABLE, otherwise returns currentSettlor.
 * Use only in user-initiated trust-type changes, NOT edit pre-population.
 */
export function resolveSettlorOnTypeChange(trustType: string, currentSettlor: string): string {
  const normalized = normalizeTrustType(trustType);
  if (normalized === "IRREVOCABLE" && currentSettlor === "JOINT") {
    return "";
  }
  return currentSettlor;
}
